import type { InstrumentSlot } from "../types";
import { allInstrumentSlots, DEFAULT_INSTRUMENT_SLOT_COUNT } from "../types";
import { defaultSlotName } from "../kernel/defaultPresets";

/** Tracker note FX (row effects) — distinct from master FX stack. */
export type NoteFxKind =
  | "vibrato"
  | "slideUp"
  | "slideDown"
  | "portamento"
  | "cut"
  | "arpeggio"
  | "filter"
  | "retrigger"
  | "gate"
  | "noteDelay"
  | "chance"
  | "pan";

export type NoteFx = {
  kind: NoteFxKind;
  /** 0–255 style value; interpretation depends on kind. */
  value: number;
};

/** One step on one instrument track. */
export type PatternCell = {
  /** MIDI note; omit when empty or note-off-only. */
  note?: number;
  /** Explicit note-off / gate close. */
  noteOff?: boolean;
  /** 0–1; omit = full / carry. */
  volume?: number;
  fx?: NoteFx[];
};

export type PatternTrack = {
  slot: InstrumentSlot;
  /** Label from `I1 Bass` lines (optional). */
  name?: string;
  cells: PatternCell[];
  /** When true, this track is muted in this pattern only (solo still overrides). */
  muted?: boolean;
};

export type Pattern = {
  id: string;
  /** Optional display name from `P0 Verse` DSL headers. */
  name?: string;
  /** Row count for this pattern (patterns may differ in length). */
  steps: number;
  tracks: PatternTrack[];
  /**
   * Optional row markers (labels + tint). Keys are step indices as decimal strings
   * so JSON / DSL stay stable.
   */
  rowMarkers?: Record<string, RowMarker>;
};

/** Named tint on a pattern row (section markers, cues, etc.). */
export type RowMarker = {
  label: string;
  /** Index into `ROW_MARKER_COLORS` (0–7). Default 0 = amber. */
  color: number;
};

/** Eight preset row-marker tints; index 0 is the default orange-ish. */
export const ROW_MARKER_COLORS = [
  "#e6a35a", // amber (default)
  "#e07a5f", // coral
  "#db6b8a", // rose
  "#9b7ed9", // violet
  "#5ba3e0", // sky
  "#4db6a0", // teal
  "#8fbc5a", // lime
  "#94a3b8", // slate
] as const;

export function clampRowMarkerColor(color: number | undefined): number {
  if (!Number.isFinite(color)) {
    return 0;
  }
  return Math.min(ROW_MARKER_COLORS.length - 1, Math.max(0, Math.round(color!)));
}

export function rowMarkerColorHex(color: number | undefined): string {
  return ROW_MARKER_COLORS[clampRowMarkerColor(color)]!;
}

export function getRowMarker(
  pattern: Pattern | undefined,
  step: number,
): RowMarker | undefined {
  const raw = pattern?.rowMarkers?.[String(step)];
  if (!raw || typeof raw.label !== "string") {
    return undefined;
  }
  const label = raw.label.trim();
  if (!label) {
    return undefined;
  }
  return { label, color: clampRowMarkerColor(raw.color) };
}

export function setRowMarker(
  pattern: Pattern,
  step: number,
  marker: RowMarker | undefined,
): void {
  if (!marker || !marker.label.trim()) {
    if (pattern.rowMarkers) {
      delete pattern.rowMarkers[String(step)];
      if (Object.keys(pattern.rowMarkers).length === 0) {
        delete pattern.rowMarkers;
      }
    }
    return;
  }
  if (!pattern.rowMarkers) {
    pattern.rowMarkers = {};
  }
  pattern.rowMarkers[String(step)] = {
    label: marker.label.trim().slice(0, 48),
    color: clampRowMarkerColor(marker.color),
  };
}

/**
 * Ordered chain of pattern refs (M8/LSDj “chain”). Same pattern id may repeat;
 * one pattern may appear in many scenes.
 */
export type Scene = {
  id: string;
  name?: string;
  /** Pattern ids in play order; repeats allowed. */
  order: string[];
};

/**
 * Compiled clip — language-agnostic IR shared by DSL, grid UI, and scheduler.
 * Host-only; kernel never sees this (only noteOn/noteOff messages).
 */
export type ClipIR = {
  bpm: number;
  /**
   * Subdivisions per beat (16 = sixteenth notes).
   * `secondsPerStep = (60 / bpm) * (4 / stepDivision)` is wrong —
   * use `(60 / bpm) / (stepDivision / 4)` = `60 / bpm * 4 / stepDivision`.
   */
  stepDivision: number;
  patterns: Pattern[];
  /** Scene library (chains of pattern refs). */
  scenes: Scene[];
  /**
   * Song: scene ids in play order.
   * Legacy clips may still have pattern ids here until `normalizeClipScenes`.
   */
  order: string[];
  loop: boolean;
};

export type ParsePatternResult = {
  clip: ClipIR;
  warnings: string[];
};

export function secondsPerStep(clip: Pick<ClipIR, "bpm" | "stepDivision">): number {
  return (60 / clip.bpm) * (4 / clip.stepDivision);
}

export function emptyCell(): PatternCell {
  return {};
}

/** Steps in one pattern (0 if missing). */
export function patternSteps(pattern: Pattern | undefined): number {
  if (!pattern || !Number.isFinite(pattern.steps)) {
    return 0;
  }
  return Math.max(0, Math.floor(pattern.steps));
}

/** Resolve a pattern by id from a clip. */
export function findPattern(clip: ClipIR, id: string | undefined): Pattern | undefined {
  if (!id) {
    return clip.patterns[0];
  }
  return clip.patterns.find(pattern => pattern.id === id) ?? clip.patterns[0];
}

/**
 * Pattern-id playback sequence for the song.
 * With scenes: expands each scene in `order`. Without: treats `order` as pattern ids (legacy).
 */
export function songPatternOrder(clip: ClipIR): string[] {
  if (clip.scenes && clip.scenes.length > 0) {
    const out: string[] = [];
    for (const sceneId of clip.order) {
      const scene = clip.scenes.find(entry => entry.id === sceneId);
      if (!scene) {
        continue;
      }
      for (const pid of scene.order) {
        if (clip.patterns.some(p => p.id === pid)) {
          out.push(pid);
        }
      }
    }
    return out;
  }
  return clip.order.filter(id => clip.patterns.some(p => p.id === id));
}

/** Total steps along the song (expanded pattern order). */
export function clipOrderSteps(clip: ClipIR): number {
  let total = 0;
  for (const id of songPatternOrder(clip)) {
    total += patternSteps(clip.patterns.find(entry => entry.id === id));
  }
  return total;
}

/**
 * Map an absolute step index within one playthrough of the song to pattern + row.
 * Returns undefined when `absolute` is out of range for a non-empty order.
 */
export function locateOrderStep(
  clip: ClipIR,
  absolute: number,
): { pattern: Pattern; patternIndex: number; row: number } | undefined {
  if (absolute < 0) {
    return undefined;
  }
  const patternIds = songPatternOrder(clip);
  let remaining = absolute;
  for (let patternIndex = 0; patternIndex < patternIds.length; patternIndex++) {
    const id = patternIds[patternIndex]!;
    const pattern = clip.patterns.find(entry => entry.id === id);
    const steps = patternSteps(pattern);
    if (!pattern || steps <= 0) {
      continue;
    }
    if (remaining < steps) {
      return { pattern, patternIndex, row: remaining };
    }
    remaining -= steps;
  }
  return undefined;
}

/** Absolute start offset of `patternId` within one song cycle (first occurrence). */
export function orderOffsetOfPattern(clip: ClipIR, patternId: string): number {
  let offset = 0;
  for (const id of songPatternOrder(clip)) {
    if (id === patternId) {
      return offset;
    }
    offset += patternSteps(clip.patterns.find(entry => entry.id === id));
  }
  return 0;
}

export function createEmptyClip(options?: {
  bpm?: number;
  steps?: number;
  stepDivision?: number;
  trackCount?: number;
}): ClipIR {
  const steps = options?.steps ?? 16;
  const stepDivision = options?.stepDivision ?? 16;
  const trackCount = options?.trackCount ?? DEFAULT_INSTRUMENT_SLOT_COUNT;
  const tracks: PatternTrack[] = allInstrumentSlots(trackCount).map(slot => ({
    slot,
    name: defaultSlotName(slot),
    cells: Array.from({ length: steps }, () => emptyCell()),
  }));
  return {
    bpm: options?.bpm ?? 120,
    stepDivision,
    patterns: [{ id: "P0", steps, tracks }],
    scenes: [{ id: "S0", order: ["P0"] }],
    order: ["S0"],
    loop: true,
  };
}
